import type { FloorTheme } from "../brand.js";
import type { Frame, Panel, Prop, PropKind, Rect, Spot } from "./furniture.js";
import type { Deco } from "./furniture.js";
import type { DecoName } from "./models.js";

/**
 * The layouts of the themed floors (lobby, software, studio, marketing, accounting, leadership):
 * where every piece of an interior architect's plan stands. Pure frame-space code like the rest of
 * `furniture.ts`: `lat` runs across the hall (negative toward the elevator side), `dep` from the
 * back wall (the elevator bank) toward the door. A piece that does not fit the room (a small
 * hall) is left out instead of overlapping something, and the corridors in front of the door, the
 * elevator fronts are reserved, so every theme stays walkable (checked by a flood fill in
 * the tests).
 */

type Face = "front" | "back" | "left" | "right";

export interface Seat {
  lat: number;
  dep: number;
}
export interface SeatPlan {
  lead: Seat | null;
  workers: Seat[];
}

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/** Where a theme seats its people (the agents' chairs; empty desks use the same plan). */
export function seatPlan(theme: FloorTheme, f: Pick<Frame, "width" | "length">): SeatPlan | null {
  const span = f.width / 2;
  const L = f.length;
  const grid = (cols: number[], rows: number[], max: number): Seat[] =>
    rows
      .flatMap((dep) => cols.map((lat) => ({ lat, dep })))
      .filter((s) => s.dep <= L - 4.5)
      .slice(0, max);
  switch (theme) {
    case "software": {
      if (f.width < 16) return null;
      const c = clamp(span * 0.5, 3.8, 6.4);
      const cols = [-c - 1.05, -c + 1.05, c - 1.05, c + 1.05];
      return {
        lead: { lat: 0, dep: 3.3 },
        workers: grid(cols, [7.6, 10.6, 13.6].slice(0, L >= 22 ? 3 : 2), 12),
      };
    }
    case "studio": {
      if (f.width < 16) return null;
      // Editing desks along the right side, two rows.
      const c = -clamp(span * 0.38, 3.4, 5.4);
      return { lead: { lat: 3.4, dep: 3.3 }, workers: grid([c - 1.05, c + 1.05], [7.8, 11], 4) };
    }
    case "marketing": {
      if (f.width < 16) return null;
      const c = clamp(span * 0.55, 4, 7);
      return {
        lead: { lat: 0, dep: 3.3 },
        workers: grid([c - 1.05, c + 1.05], [7.6, 10.6], 4),
      };
    }
    case "accounting": {
      if (f.width < 16) return null;
      // Quiet desks in rows in the open hall; the closed offices hold the lead.
      const cols = [-3.3, -0.3, 2.7, 5.7].map((l) => clamp(l, -span + 2, span - 2));
      return { lead: { lat: 0, dep: 3.3 }, workers: grid(cols, [8.4, 11.4], 8) };
    }
    case "leadership":
      return f.width >= 14 && f.length >= 12
        ? {
            lead: { lat: 0, dep: 3.3 },
            workers: [
              { lat: -3.4, dep: 5.9 },
              { lat: 3.4, dep: 5.9 },
            ],
          }
        : null;
    case "lobby": {
      const rl = clamp(span - 4.2, 3.6, 7.5);
      const line = L - 8.2;
      return {
        lead: { lat: rl, dep: L - 7.0 },
        workers: f.width >= 18 && L >= 16 ? [{ lat: 3.9, dep: line + 0.55 }] : [],
      };
    }
    default:
      return null;
  }
}

/** Everything a theme needs from `furnitureLayout`: placement helpers over the frame and the lists. */
export interface Ctx {
  f: Frame;
  span: number;
  L: number;
  out: {
    props: Prop[];
    panels: Panel[];
    solids: Rect[];
    sofas: Spot[];
    armchairs: Spot[];
    coffeeTables: Spot[];
    bookshelves: Spot[];
    shelves: Spot[];
    boards: Spot[];
    coffeeCarts: Spot[];
    fridges: Spot[];
    meetingChairs: Spot[];
    rugs: (Rect & { color: string })[];
    plants: { x: number; z: number; scale: number }[];
    pendants: { x: number; z: number }[];
    desks: Spot[];
    chairs: Spot[];
    monitors: Spot[];
    keyboards: Spot[];
    mice: Spot[];
    table: Spot | null;
    deco: Deco[];
    display: { x: number; z: number; yaw: number } | null;
  };
  /** Adds a prop when the rectangle is free; false (and nothing added) when it is not. */
  put(
    kind: PropKind,
    lat: number,
    dep: number,
    o?: {
      w?: number;
      d?: number;
      h?: number;
      face?: Face;
      tone?: Prop["tone"];
      solid?: boolean;
      y?: number;
    },
  ): boolean;
  /** A real model (sofa, armchair...) with its solid footprint `w` x `d` (model frame). */
  model(
    list:
      | "sofas"
      | "armchairs"
      | "coffeeTables"
      | "bookshelves"
      | "shelves"
      | "boards"
      | "coffeeCarts"
      | "fridges"
      | "meetingChairs",
    lat: number,
    dep: number,
    face: Face,
    w: number,
    d: number,
    o?: { solid?: boolean; scale?: number },
  ): boolean;
  /**
   * A Poly Haven model with its solid footprint `w` x `d` (model frame). `on` puts it on top of
   * something already placed (a cabinet, a table): no space test, no collider, `y` is the height.
   */
  deco(
    name: DecoName,
    lat: number,
    dep: number,
    face: Face,
    w: number,
    d: number,
    o?: { solid?: boolean; scale?: number; y?: number; on?: boolean },
  ): boolean;
  panel(
    kind: Panel["kind"],
    lat: number,
    dep: number,
    face: Face,
    w: number,
    h: number,
    y: number,
    tone?: Panel["tone"],
  ): void;
  rug(lat: number, dep: number, a: number, b: number, color: string): void;
  plant(lat: number, dep: number, scale?: number): boolean;
  pendant(lat: number, dep: number): void;
  /** An office chair facing the door (a seat without a desk). */
  chair(lat: number, dep: number): void;
  /** A workstation (chair, desk, monitors, keyboard, mouse) facing the door. */
  station(lat: number, dep: number, o?: { scale?: number; dual?: boolean }): void;
  /** Free area test (frame rectangle centred on lat/dep, `a` across, `b` deep). */
  free(lat: number, dep: number, a: number, b: number): boolean;
  /** A glass-fronted room: panes on the sides listed, a gap `door` wide in the front one. */
  glassRoom(
    lat: number,
    dep: number,
    a: number,
    b: number,
    o?: { open?: Face; gap?: number; sides?: Face[] },
  ): void;
  /** Reserve (without drawing) a rectangle, for a view or a corridor to stay open. */
  reserve(lat: number, dep: number, a: number, b: number): void;
}

const GREENS = ["calathea", "planterBox"] as const;
const isGreen = (n: string) => (GREENS as readonly string[]).includes(n);

/**
 * Tops the floor's greenery up to `need` plants (the floor reads as lived in): calathea and planter boxes along the side walls and in the corners, only where there
 * is free room (the reachability corridors stay open).
 */
function greenery(c: Ctx, need: number): void {
  const have = () => c.out.plants.length + c.out.deco.filter((d) => isGreen(d.name)).length;
  const deps = [5.8, 8.4, 11.0, 13.6, 16.2, c.L - 1.2, c.L - 2.8];
  const sizes = { calathea: 0.8, planterBox: 0.9 } as const;
  let k = 0;
  for (const dep of deps) {
    for (const side of [-1, 1]) {
      if (have() >= need) return;
      const name = GREENS[k++ % GREENS.length]!;
      const z = sizes[name];
      c.deco(name, side * (c.span - 0.9), dep, "front", z, name === "planterBox" ? 0.4 : z);
    }
  }
}

const P = "P";
const A = "A";

/** Runs the layout of `theme`. */
export function layoutTheme(theme: FloorTheme, c: Ctx): void {
  switch (theme) {
    case "lobby":
      return lobby(c);
    case "software":
      return software(c);
    case "studio":
      return studio(c);
    case "marketing":
      return marketing(c);
    case "accounting":
      return accounting(c);
    case "leadership":
      return leadership(c);
    default:
      return;
  }
}

/** Frame positions near the walls. */
const wallL = (c: Ctx, inset = 0) => -c.span + inset;
const wallR = (c: Ctx, inset = 0) => c.span - inset;

function lobby(c: Ctx) {
  const { span, L } = c;
  const seats = seatPlan("lobby", c.f);
  for (const s of [seats?.lead, ...(seats?.workers ?? [])]) if (s) c.chair(s.lat, s.dep);
  // The security line: turnstiles with glass wings to both side walls, lanes toward the elevators.
  const line = L - 8.2;
  const big = c.f.width >= 18 && L >= 16;
  if (big) {
    const lanes = [-1.6, 0, 1.6];
    for (const lat of lanes) c.put("turnstile", lat, line, { w: 0.34, d: 1.2 });
    // Glass wings from the outer lanes to the walls (the elevator side keeps a staff door gap).
    const leftEnd = -2.45;
    const rightEnd = 2.45;
    c.put("glassPanel", (leftEnd + wallL(c, 0.3)) / 2 - 0.0, line, {
      w: leftEnd - wallL(c, 0.3),
      d: 0.08,
      h: 1.2,
    });
    c.put("glassPanel", (rightEnd + wallR(c, 0.3)) / 2, line, {
      w: wallR(c, 0.3) - rightEnd,
      d: 0.08,
      h: 1.2,
    });
    // Security desk beside the lanes, on the entrance side.
    c.put("counter", 3.9, line + 1.5, { w: 2.2, d: 0.8, tone: P });
    c.panel("logoPlate", 3.9, line + 1.5 + 0.41, "front", 1.6, 0.4, 0.55, "P");
  }
  // Reception desk: right front, with the brand plate on its face and a monitor behind.
  const rl = clamp(span - 4.2, 3.6, 7.5);
  if (c.put("counter", rl, L - 5.8, { w: 4.4, d: 1.1, tone: P, face: "front" })) {
    c.panel("logoPlate", rl, L - 5.8 + 0.56, "front", 3.2, 0.6, 0.55, "P");
    c.put("deskScreen", rl - 0.8, L - 5.8 - 0.1, { w: 0.5, d: 0.2, y: 1.1, solid: false });
    c.put("deskScreen", rl + 0.8, L - 5.8 - 0.1, { w: 0.5, d: 0.2, y: 1.1, solid: false });
  }
  // Waiting lounge: left front, a long sofa, two armchairs and a table on a rug.
  const ll = -clamp(span - 4.4, 3.6, 7.5);
  const ld = L - 5.2;
  c.rug(ll, ld, 6.0, 4.0, "#e5e0d6");
  c.deco("sofa3", ll, ld - 1.7, "front", 2.4, 0.85);
  if (c.deco("coffeeRound", ll, ld, "front", 0.95, 0.95)) {
    c.deco("teaSet", ll, ld, "front", 0.45, 0.3, { on: true, y: 0.36 });
  }
  c.deco("loungeChair", ll - 2.3, ld, "right", 0.95, 0.95);
  c.deco("loungeChair", ll + 2.3, ld, "left", 0.95, 0.95);
  c.pendant(ll, ld);
  c.deco("pachira", ll - 3.3, ld - 1.4, "front", 0.7, 0.7, { scale: 1.1 });
  c.deco("calathea", wallL(c, 0.9), L - 1.2, "front", 0.8, 0.8);
  c.deco("pachira", wallR(c, 0.9), L - 1.2, "front", 0.7, 0.7);
  // Behind the line: the logo wall on the back wall, the directory, plants and benches.
  const mid = big ? 0 : 0;
  c.panel("logoWall", mid, 0.1, "front", clamp(c.f.width * 0.34, 5, 8.4), 3.0, 0.5);
  c.put("accentWall", mid, 0.07, {
    w: clamp(c.f.width * 0.34, 5, 8.4) + 0.5,
    d: 0.04,
    h: 3.4,
    tone: P,
    solid: false,
  });
  c.panel("directory", -5.0, 0.1, "front", 2.0, 1.7, 1.0);
  c.put("planter", mid - clamp(c.f.width * 0.17, 2.6, 4.2) - 0.9, 1.0, { w: 1.2, d: 0.8, tone: A });
  c.put("planter", mid + clamp(c.f.width * 0.17, 2.6, 4.2) + 0.9, 1.0, { w: 1.2, d: 0.8, tone: A });
  c.put("loungeBench", mid, 3.4, { w: 3.0, d: 0.7, tone: A });
  c.pendant(0, 4);
  c.rug(0, 4.4, 5.4, 3.0, "#d8d3c8");
  // A marble bust and a brass vase on a walnut cabinet under the directory.
  if (c.deco("cabinet", -5.0, 0.5, "front", 2.0, 0.45)) {
    c.deco("bust", -5.5, 0.5, "front", 0.3, 0.3, { on: true, y: 0.56 });
    c.deco("vaseBrass", -4.5, 0.5, "front", 0.25, 0.25, { on: true, y: 0.56 });
  }
  c.deco("plant4", mid - 3.4, 1.0, "front", 0.4, 0.4);
  c.deco("pachira", wallL(c, 0.9), 5.6, "front", 0.7, 0.7);
  c.deco("plant4", rl + 2.6, L - 7.4, "front", 0.4, 0.4);
  c.deco("calathea", mid + 3.9, 1.0, "front", 0.8, 0.8);
  greenery(c, 11);
}

function software(c: Ctx) {
  const { span, L, f } = c;
  const plan = seatPlan("software", f);
  if (!plan) return;
  const lead = plan.lead!;
  c.station(lead.lat, lead.dep, { scale: 1.1, dual: true });
  for (const s of plan.workers) {
    c.station(s.lat, s.dep, { dual: true });
  }
  // Low dividers between the two desks of a pod, so the pods read as clusters.
  const cols = [...new Set(plan.workers.map((s) => s.lat))].sort((a, b) => a - b);
  const rows = [...new Set(plan.workers.map((s) => s.dep))];
  for (let i = 0; i + 1 < cols.length; i += 2) {
    for (const dep of rows) {
      c.put("divider", (cols[i]! + cols[i + 1]!) / 2, dep + 0.6, {
        w: 0.05,
        d: 1.0,
        solid: false,
        tone: A,
      });
    }
  }
  // Pod rugs in the brand color.
  if (cols.length >= 2) {
    const podC = (cols[0]! + cols[1]!) / 2;
    const depMid = rows.length > 0 ? (Math.min(...rows) + Math.max(...rows)) / 2 + 0.6 : 9;
    const depLen = rows.length > 0 ? Math.max(...rows) - Math.min(...rows) + 3.6 : 4;
    c.rug(podC, depMid, 4.6, depLen, "#2a3140");
    if (cols.length >= 4) c.rug((cols[2]! + cols[3]!) / 2, depMid, 4.6, depLen, "#2a3140");
  }
  // Whiteboards on the back wall beside the lead.
  c.panel("whiteboard", -4.2, 0.1, "front", 2.6, 1.3, 1.0);
  c.panel("whiteboard", 4.2, 0.1, "front", 2.6, 1.3, 1.0);
  c.panel("logoPlate", 0, 0.1, "front", 2.4, 0.6, 2.3, "P");
  // Server room behind glass on the elevator side: racks with blinking LEDs.
  const sl = wallL(c, 1.25);
  const sd = 8.0;
  if (c.free(sl, sd, 2.5, 4.2)) {
    c.put("serverRack", sl - 0.2, sd - 1.3, { w: 0.65, d: 1.05, face: "right" });
    c.put("serverRack", sl - 0.2, sd - 0.1, { w: 0.65, d: 1.05, face: "right" });
    c.put("serverRack", sl - 0.2, sd + 1.1, { w: 0.65, d: 1.05, face: "right" });
    c.glassRoom(sl, sd, 2.5, 4.2, { sides: ["right", "front"] });
    c.put("floorGlow", sl, sd, { w: 2.2, d: 3.9, tone: A, solid: false, h: 0 });
  }
  // Focus booths on the second-cabin side.
  const bl = wallR(c, 1.2);
  for (const dep of [7.2, 9.8]) c.put("booth", bl, dep, { w: 1.9, d: 1.9, face: "left", tone: P });
  // Kitchenette along the left wall near the door, a fridge and a coffee cart.
  if (L >= 16) {
    const kd = L - 4.4;
    c.put("kitchen", wallL(c, 0.45), kd, { w: 4.4, d: 0.9, face: "right" });
    c.model("fridges", wallL(c, 0.6), kd - 3.0, "right", 0.8, 0.8);
    c.model("coffeeCarts", wallL(c, 1.2), kd + 3.0, "right", 2.1, 1.0);
    c.put("island", wallL(c, 2.9), kd, { w: 0.9, d: 2.4, face: "right" });
    for (const k of [-0.7, 0.7]) c.put("stool", wallL(c, 3.7), kd + k, { w: 0.4, d: 0.4 });
  }
  // A break lounge on the right front.
  const rl = wallR(c, 3.6);
  const rd = L - 4.6;
  if (c.free(rl, rd, 5.0, 3.6)) {
    c.rug(rl, rd, 5.0, 3.6, "#cfd6e2");
    c.model("sofas", rl, rd - 1.5, "front", 1.9, 0.9);
    c.model("coffeeTables", rl, rd, "front", 1.1, 0.6);
    c.model("armchairs", rl - 1.9, rd, "right", 0.9, 0.9);
    c.pendant(rl, rd);
  }
  void span;
  c.plant(wallR(c, 0.9), L - 1.2, 1.1);
  c.plant(wallL(c, 0.9), 11.6, 1.0);
  // A calathea at the door; the rest of the greenery comes from `greenery`.
  c.deco("calathea", wallL(c, 1.0), L - 1.2, "front", 0.8, 0.8);
  greenery(c, 11);
}

function studio(c: Ctx) {
  const { span, L, f } = c;
  const plan = seatPlan("studio", f);
  // Recording booth, glass-fronted, back right corner area next to the second-cabin front.
  const bw = 4.4;
  const bd = 3.6;
  const bl = wallR(c, bd / 2 + 0.2);
  c.put("recBooth", bl, 8.2, { w: bw, d: bd, face: "left" });
  // Green screen cove on the back wall, left of centre, with tripod and lights in front.
  const gl = -3.0;
  c.put("greenScreen", gl, 0.2, { w: 5.0, h: 3.0, d: 0.1, solid: false });
  c.reserve(gl, 1.9, 5.0, 3.6);
  if (c.put("tripod", gl, 3.2, { w: 0.9, d: 0.9 })) {
    c.deco("camera", gl, 3.2, "front", 0.3, 0.3, { on: true, y: 1.45, scale: 1 });
  }
  c.put("softbox", gl - 2.3, 2.6, { w: 0.9, d: 0.9 });
  c.put("softbox", gl + 2.3, 2.6, { w: 0.9, d: 0.9 });
  c.put("tripod", gl + 3.7, 5.2, { w: 0.9, d: 0.9 });
  // Acoustic slat panels on the walls.
  c.panel("acoustic", wallL(c, 0.05), 11.0, "right", 5.0, 1.8, 1.1);
  c.panel("acoustic", wallL(c, 0.05), 15.0, "right", 3.6, 1.8, 1.1);
  c.panel("acoustic", wallR(c, 0.05), 15.0, "left", 3.6, 1.8, 1.1);
  c.panel("logoPlate", 4.4, 0.1, "front", 2.4, 0.6, 2.4, "P");
  // Editing desks: two big monitors each.
  if (plan) {
    const lead = plan.lead!;
    c.station(lead.lat, lead.dep, { scale: 1.1, dual: true });
    for (const s of plan.workers) c.station(s.lat, s.dep, { scale: 1.0, dual: true });
    const cs = [...new Set(plan.workers.map((s) => s.lat))];
    const mid = cs.length > 0 ? cs.reduce((a, b) => a + b, 0) / cs.length : 0;
    c.rug(mid, 9.7, 5.0, 5.4, "#1f232a");
  }
  // Wall of screens on the left side wall in the editing area.
  c.put("screenWall", wallL(c, 0.12), 8.2, {
    w: 4.8,
    d: 0.12,
    h: 1.8,
    face: "right",
    y: 1.0,
    solid: false,
  });
  // Dark lounge by the door with a long sofa facing a screen.
  const ll = -clamp(span - 4.8, 3.2, 6);
  const ld = L - 4.2;
  if (c.free(ll, ld, 5.0, 3.6)) {
    c.rug(ll, ld, 5.0, 3.6, "#2b2f38");
    c.deco("sofa3", ll, ld - 1.4, "front", 2.4, 0.85);
    c.deco("coffeeLong", ll, ld, "front", 1.1, 0.55);
    c.pendant(ll, ld);
  }
  c.put("equipCase", wallR(c, 0.7), L - 5.0, { w: 1.2, d: 0.7, face: "left" });
  c.put("equipCase", wallR(c, 0.7), L - 6.0, { w: 1.2, d: 0.7, face: "left" });
  c.plant(wallR(c, 0.9), L - 1.2, 1.1);
  // A projector screen for playback, steel shelving for gear, planters and bar chairs.
  c.deco("projector", 1.8, 5.4, "front", 1.1, 0.6);
  c.deco("steelShelves", wallR(c, 0.45), 11.9, "left", 1.1, 0.5);
  c.deco("planterBox", -6.2, 2.2, "front", 0.9, 0.4);
  c.deco("calathea", wallL(c, 1.0), L - 1.2, "front", 0.8, 0.8);
  for (const k of [-0.6, 0.6]) c.deco("barChair", ll + 3.0, ld + k, "front", 0.4, 0.4);
  greenery(c, 11);
}

function marketing(c: Ctx) {
  const { span, L, f } = c;
  const plan = seatPlan("marketing", f);
  // Brand accent wall at the back, with a mood board and a campaign board.
  const wallW = clamp(f.width * 0.5, 6, 11);
  c.put("accentWall", 0, 0.07, { w: wallW, d: 0.04, h: 3.4, tone: A, solid: false });
  c.panel("moodBoard", -wallW * 0.22, 0.1, "front", 3.0, 1.9, 1.0);
  c.panel("campaign", wallW * 0.2, 0.1, "front", 2.4, 1.9, 1.0);
  c.panel("logoPlate", 0, 0.1, "front", 2.0, 0.6, 2.8, "P");
  if (plan) {
    const lead = plan.lead!;
    c.station(lead.lat, lead.dep, { scale: 1.1 });
    for (const s of plan.workers) c.station(s.lat, s.dep);
  }
  // Campaign table with chairs in the middle of the hall on a brand rug.
  const tl = -clamp(span * 0.42, 3, 6);
  const td = 10.6;
  if (c.free(tl, td, 4.8, 3.6)) {
    c.rug(tl, td, 5.4, 4.2, "A");
    c.put("longTable", tl, td, { w: 3.2, d: 1.3 });
    for (const k of [-1, 0, 1]) {
      c.model("meetingChairs", tl + k * 1.0, td - 1.0, "front", 0.5, 0.5, { solid: false });
      c.model("meetingChairs", tl + k * 1.0, td + 1.0, "back", 0.5, 0.5, { solid: false });
    }
    c.pendant(tl, td);
  }
  // Big wall screens on the left wall.
  c.put("screenWall", wallL(c, 0.12), 12.8, {
    w: 5.2,
    d: 0.12,
    h: 1.9,
    face: "right",
    y: 1.0,
    solid: false,
  });
  // Lounge seating: pouf cluster and sofas, brand colored, front right.
  const rl = clamp(span - 4.6, 3.2, 6.5);
  const rd = L - 4.4;
  if (c.free(rl, rd, 5.4, 4.2)) {
    c.rug(rl, rd, 5.6, 4.4, "P");
    if (c.deco("sofa3", rl, rd - 1.7, "front", 2.4, 0.85)) {
      c.deco("pillows", rl, rd - 1.7, "front", 0.9, 0.4, { on: true, y: 0.42 });
    }
    c.model("sofas", rl - 2.6, rd, "right", 1.9, 0.9);
    c.deco("coffeeLong", rl, rd, "front", 1.1, 0.55);
    c.put("pouf", rl + 1.7, rd - 0.6, { w: 0.7, d: 0.7, tone: P });
    c.deco("ottoman", rl + 1.5, rd + 0.6, "front", 0.8, 0.56);
    c.pendant(rl, rd);
  }
  // A coffee bar by the door on the left, and a whiteboard.
  c.model("coffeeCarts", wallL(c, 1.4), L - 3.0, "right", 2.1, 1.0);
  c.put("island", wallL(c, 0.5), L - 5.6, { w: 0.9, d: 2.2, face: "right" });
  c.panel("whiteboard", wallR(c, 0.05), 11.4, "left", 2.6, 1.3, 1.0);
  c.plant(wallR(c, 0.9), L - 1.2, 1.2);
  c.plant(wallL(c, 0.9), 8.2, 1.0);
  // Brand art on the wall of the campaign table, a round table for quick chats, greenery.
  c.deco("roundTable", wallR(c, 2.6), 7.0, "front", 1.1, 1.1);
  c.deco("calathea", wallL(c, 1.0), L - 1.2, "front", 0.8, 0.8);
  c.deco("planterBox", -1.0, 5.2, "front", 0.9, 0.4);
  greenery(c, 11);
}

function accounting(c: Ctx) {
  const { span, L, f } = c;
  const plan = seatPlan("accounting", f);
  if (plan) {
    for (const s of plan.workers) c.station(s.lat, s.dep);
  }
  // Closed offices with glass fronts along the left wall, each with a desk and a filing cabinet.
  const ow = 4.0;
  const od = 3.6;
  const ol = wallL(c, ow / 2 + 0.2);
  const offices = [{ dep: 7.6 }, { dep: 7.6 + od + 0.4 }].filter((o) => o.dep + od / 2 < L - 3.2);
  offices.forEach((o) => {
    if (!c.free(ol, o.dep, ow, od)) return;
    c.glassRoom(ol, o.dep, ow, od, { open: "right", gap: 1.1, sides: ["right"] });
    c.put("wallPanel", ol - ow / 2 + 0.05, o.dep, {
      w: od,
      d: 0.06,
      h: 2.6,
      face: "right",
      solid: true,
      tone: A,
    });
    c.put("wallPanel", ol, o.dep - od / 2 + 0.05, {
      w: ow,
      d: 0.06,
      h: 2.6,
      face: "front",
      solid: true,
      tone: A,
    });
    c.put("wallPanel", ol, o.dep + od / 2 - 0.05, {
      w: ow,
      d: 0.06,
      h: 2.6,
      face: "front",
      solid: true,
      tone: A,
    });
    c.station(ol - 0.4, o.dep - 0.9, {});
    c.put("filing", ol - ow / 2 + 0.55, o.dep + 0.9, { w: 1.1, d: 0.6, face: "right" });
  });
  // The lead's room (the corner office): glass front, behind the back wall.
  const lead = plan?.lead;
  if (lead) {
    c.station(lead.lat, lead.dep, { scale: 1.1 });
    c.deco("deskLamp", lead.lat - 0.7, lead.dep + 0.55, "front", 0.2, 0.2, { on: true, y: 0.8 });
  }
  if (c.deco("cabinet", 2.6, 0.5, "front", 2.0, 0.45)) {
    c.deco("books", 2.2, 0.5, "front", 0.5, 0.2, { on: true, y: 0.56 });
  }
  c.put("filing", 5.2, 0.5, { w: 1.1, d: 0.6 });
  c.panel("logoPlate", -2.8, 0.1, "front", 2.4, 0.6, 2.4, "P");
  c.panel("whiteboard", 3.6, 0.1, "front", 2.2, 1.2, 1.2);
  // The meeting room, glass walls, on the right side.
  const mw = 4.6;
  const md = 5.0;
  const ml = wallR(c, mw / 2 + 0.2);
  const mdp = 13.0;
  if (L >= 19 && c.free(ml, mdp, mw, md)) {
    c.glassRoom(ml, mdp, mw, md, { open: "left", gap: 1.2, sides: ["left", "back", "front"] });
    c.put("longTable", ml + 0.2, mdp, { w: 1.2, d: 2.6 });
    for (const k of [-0.9, 0, 0.9]) {
      c.model("meetingChairs", ml - 0.5, mdp + k, "right", 0.5, 0.5, { solid: false });
      c.model("meetingChairs", ml + 0.9, mdp + k, "left", 0.5, 0.5, { solid: false });
    }
    c.rug(ml, mdp, mw - 0.3, md - 0.3, "#d9d3c4");
  }
  // A quiet lounge with a few chairs and a water cooler by the door.
  const ll = -clamp(span - 4.4, 3, 6);
  if (c.free(ll, L - 3.4, 5.0, 2.8)) {
    c.deco("sofa3", ll, L - 3.4, "front", 2.4, 0.85);
    c.deco("loungeChair", ll + 2.3, L - 3.4, "front", 0.95, 0.95);
    c.put("cooler", ll - 2.0, L - 3.4, { w: 0.4, d: 0.4 });
    c.deco("sideTable", ll + 3.3, L - 3.4, "front", 0.5, 0.42);
  }
  c.put("planter", wallR(c, 0.6), L - 1.6, { w: 0.9, d: 1.2, tone: A });
  c.deco("calathea", wallL(c, 1.0), 5.6, "front", 0.8, 0.8);
  c.deco("pachira", wallR(c, 1.0), 6.8, "front", 0.7, 0.7);
  c.deco("planterBox", 0.4, L - 1.2, "front", 0.9, 0.4);
  void f;
  greenery(c, 11);
}

function leadership(c: Ctx) {
  const { span, L, f } = c;
  // Corner offices: two glass-fronted rooms in the back corners' neighbourhood is taken by the lift
  // core, so the front corners hold them; the boardroom is in the middle of the front.
  const od = 5.0;
  const od0 = 7.8;
  // The leader's desk (the lead sits in the middle of the back, with a credenza and shelves).
  const plan = seatPlan("leadership", f);
  c.station(0, 3.3, { scale: 1.15 });
  for (const w of plan?.workers ?? []) c.station(w.lat, w.dep, {});
  c.deco("displayShelves", -3.2, 0.4, "front", 1.0, 0.35);
  c.deco("displayShelves", 3.2, 0.4, "front", 1.0, 0.35);
  if (c.deco("cabinet", 0, 0.5, "front", 2.0, 0.45)) {
    c.deco("statueWhale", -0.55, 0.5, "front", 0.3, 0.5, { on: true, y: 0.56 });
    c.deco("books", 0.45, 0.5, "front", 0.5, 0.2, { on: true, y: 0.56 });
  }
  c.deco("frameFancy", -5.4, 0.06, "front", 0.7, 0.04, { on: true, y: 1.4 });
  c.deco("deskLamp", -0.9, 3.85, "front", 0.2, 0.2, { on: true, y: 0.8, scale: 1.15 });
  c.panel("logoPlate", 0, 0.12, "front", 2.4, 0.6, 2.2, "P");
  c.rug(0, 3.4, 4.4, 3.2, "#2a2e38");
  // Boardroom on the right: a long table with eight chairs behind glass and a display.
  const bw = clamp(span * 0.62, 4.6, 7.4);
  const bd = 6.6;
  const bl = wallR(c, bw / 2 + 0.2);
  const bdp = od0 + 3.4;
  if (c.free(bl, bdp, bw, bd)) {
    c.glassRoom(bl, bdp, bw, bd, { open: "left", gap: 1.3, sides: ["left", "back", "front"] });
    c.put("longTable", bl, bdp, { w: 1.5, d: 4.2 });
    for (const k of [-1.5, -0.5, 0.5, 1.5]) {
      c.model("meetingChairs", bl - 1.15, bdp + k, "right", 0.5, 0.5, { solid: false });
      c.model("meetingChairs", bl + 1.15, bdp + k, "left", 0.5, 0.5, { solid: false });
    }
    c.rug(bl, bdp, bw - 0.3, bd - 0.3, "#2f343d");
    c.put("screenWall", wallR(c, 0.1), bdp, {
      w: 3.6,
      d: 0.1,
      h: 1.5,
      face: "left",
      y: 1.1,
      solid: false,
    });
    c.pendant(bl, bdp - 1);
    c.pendant(bl, bdp + 1.4);
  }
  // Corner office on the left: desk, sofa, bookcase, behind glass.
  const cw = clamp(span * 0.55, 4.4, 6.4);
  const cl = wallL(c, cw / 2 + 0.2);
  const cdp = od0 + 2.6;
  if (c.free(cl, cdp, cw, od)) {
    c.glassRoom(cl, cdp, cw, od, { open: "right", gap: 1.2, sides: ["right", "back", "front"] });
    c.station(cl - 0.5, cdp - 1.2, {});
    c.deco("displayShelves", wallL(c, 0.4), cdp + 1.6, "right", 1.0, 0.35);
    c.deco("bust", cl - 1.2, cdp + 1.0, "front", 0.3, 0.3, { scale: 1.0 });
    c.rug(cl, cdp, cw - 0.4, od - 0.4, "#3a3f4a");
  }
  // The lounge in front: sofas around a low table, a drinks cabinet, panoramic outlook.
  const ld = L - 4.4;
  if (c.free(0, ld, 7.0, 4.4)) {
    c.rug(0, ld, 6.4, 4.0, "#2a2e38");
    c.deco("sofa3", -1.95, ld - 1.4, "front", 2.4, 0.85);
    c.deco("sofa3", 1.95, ld - 1.4, "front", 2.4, 0.85);
    if (c.deco("coffeeRound", 0, ld, "front", 0.95, 0.95)) {
      c.deco("teaSet", 0, ld, "front", 0.45, 0.3, { on: true, y: 0.36 });
    }
    c.deco("loungeChair", -3.3, ld + 0.5, "right", 0.95, 0.95);
    c.deco("loungeChair", 3.3, ld + 0.5, "left", 0.95, 0.95);
    c.deco("ottoman", -0.9, ld + 1.4, "front", 0.8, 0.56);
    c.deco("statueRay", 0.9, ld + 1.4, "front", 0.55, 0.45, { solid: false });
    c.put("floorLamp", -4.0, ld - 0.6, { w: 0.4, d: 0.4 });
    c.put("floorLamp", 4.0, ld - 0.6, { w: 0.4, d: 0.4 });
    c.pendant(0, ld);
  }
  c.deco("pachira", wallL(c, 0.9), L - 1.2, "front", 0.7, 0.7, { scale: 1.2 });
  c.deco("pachira", wallR(c, 0.9), L - 1.2, "front", 0.7, 0.7, { scale: 1.2 });
  c.deco("calathea", wallL(c, 1.0), 5.0, "front", 0.8, 0.8);
  void f;
  greenery(c, 11);
}
